import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Box,
  DialogActions,
  Checkbox,
  ListItem,
  ListItemText,
  ListItemButton,
  TextField,
  InputAdornment,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaginatedApi } from 'src/hooks/use-paginated-api';
import { useSSRPaginatedInfiniteScrolling } from 'src/hooks/use-ssr-paginated-infinite-scrolling';
import { useAuthContext } from 'src/auth/hooks';
import { ICompetencyItem } from 'src/types/competency';
import { AuthorizationLevel } from 'src/types/authorization';
import axios, { endpoints } from 'src/utils/axios';
import { Iconify } from '../iconify';
import { getCompetencyName, mergeCompetencyItems, reorderCompetenciesWithPinnedTop, filterCompetenciesBySearch, formatSchemeLabels } from 'src/utils/competency';
import { CompetencyDTO } from 'src/types/dto';
import { useGetCompetencyForStandardTemplates } from 'src/actions/competency';

interface CompetencyModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (competencies: ICompetencyItem[]) => void;
  activityId?: number;
  code?: string;
  initialCompetencies?: ICompetencyItem[];
  hasLevels?: boolean;
  standardTemplateId?: number;
  standardTemplateIds?: number[];
}

export default function CompetencyModal({
  open,
  onClose,
  onSave,
  activityId,
  code,
  initialCompetencies: propInitialCompetencies,
  hasLevels = false,
  standardTemplateId,
  standardTemplateIds,
}: CompetencyModalProps) {
  const { user } = useAuthContext();
  const virtuosoRef = useRef(null);
  const { t } = useTranslation('competency');

  const [searchValue, setSearchValue] = useState<string>('');
  const [filterByScheme, setFilterByScheme] = useState(true);
  const effectiveStandardTemplateIds = useMemo(() => {
    if (standardTemplateIds?.length) {
      return Array.from(new Set(standardTemplateIds.filter((id): id is number => id != null)));
    }

    return standardTemplateId != null ? [standardTemplateId] : [];
  }, [standardTemplateId, standardTemplateIds]);
  const useSchemeFilteredList = effectiveStandardTemplateIds.length > 0 && filterByScheme;

  const {
    standardCompetencies,
    standardCompetenciesLoading,
    standardCompetenciesError,
  } = useGetCompetencyForStandardTemplates(
    open && useSchemeFilteredList ? effectiveStandardTemplateIds : undefined,
  );
  const catalogCacheRef = useRef<Map<number | string, ICompetencyItem>>(new Map());
  const {
    data: competencyData,
    page,
    pageSize,
    onPageLoaded,
    onPageChange,
    onReset,
    total,
    resetNonce,
  } = useSSRPaginatedInfiniteScrolling(100);

  const {
    data: competencies,
    pagination: competencyPagination,
    isLoading: competencysLoading,
    error: competencyError,
    mutate: competencyMutate,
  } = usePaginatedApi({
    endpoint: endpoints.competency.root,
    paginationParams: {
      page: page + 1,
      take: pageSize,
    },
    additionalParams: {
      clientId: user?.client?.id?.toString() || '',
    },
    filterParams: {
      search: searchValue.trim() || undefined,
    },
    enabled: open && !useSchemeFilteredList,
  });

  const filteredSchemeCompetencies = useMemo(
    () => (useSchemeFilteredList ? filterCompetenciesBySearch(standardCompetencies, searchValue) : []),
    [useSchemeFilteredList, standardCompetencies, searchValue],
  );

  const catalogCompetencyData = useSchemeFilteredList ? filteredSchemeCompetencies : competencyData;
  const catalogTotal = useSchemeFilteredList ? filteredSchemeCompetencies.length : total;
  const catalogLoading = useSchemeFilteredList ? standardCompetenciesLoading : competencysLoading;
  const catalogError = useSchemeFilteredList ? standardCompetenciesError : competencyError;

  const [selectedCompetencies, setSelectedCompetencies] = useState<ICompetencyItem[]>([]);
  const [initialCompetencies, setInitialCompetencies] = useState<ICompetencyItem[]>([]);
  const [editingRow, setEditingRow] = useState<(ICompetencyItem & { isNew: boolean }) | null>(null);
  const [newCompetencyData, setNewCompetencyData] = useState({
    name: '',
    externalId: '',
    description: '',
  });
  const [tempCompetencies, setTempCompetencies] = useState<ICompetencyItem[]>([]);
  const [competencyLevels, setCompetencyLevels] = useState<Record<string, AuthorizationLevel | null>>({});

  const pinnedCompetencies = useMemo(() => {
    const selectedById = new Map(
      selectedCompetencies.map((competency) => [competency.id, competency]),
    );

    return initialCompetencies
      .filter((competency) => selectedById.has(competency.id))
      .map((competency) => selectedById.get(competency.id)!);
  }, [initialCompetencies, selectedCompetencies]);

  useEffect(() => {
    if (!open) {
      catalogCacheRef.current.clear();
      return;
    }

    const upsertCatalogCache = (competency: ICompetencyItem) => {
      catalogCacheRef.current.set(
        competency.id,
        mergeCompetencyItems(catalogCacheRef.current.get(competency.id) ?? competency, competency),
      );
    };

    (propInitialCompetencies ?? []).forEach(upsertCatalogCache);
    catalogCompetencyData.forEach(upsertCatalogCache);
  }, [open, propInitialCompetencies, catalogCompetencyData]);

  const displayCompetencies = useMemo(() => {
    const seenIds = new Set<number | string>();
    const catalogCompetencies: ICompetencyItem[] = [];

    const addCatalogCompetency = (competency: ICompetencyItem) => {
      if (seenIds.has(competency.id)) {
        return;
      }
      seenIds.add(competency.id);
      catalogCompetencies.push(competency);
    };

    tempCompetencies.forEach(addCatalogCompetency);
    catalogCompetencyData.forEach(addCatalogCompetency);

    selectedCompetencies.forEach((competency) => {
      const cached = catalogCacheRef.current.get(competency.id);
      if (cached) {
        addCatalogCompetency(mergeCompetencyItems(competency, cached));
      }
    });

    return reorderCompetenciesWithPinnedTop(catalogCompetencies, pinnedCompetencies);
  }, [tempCompetencies, catalogCompetencyData, pinnedCompetencies, selectedCompetencies, resetNonce]);

  useEffect(() => {
    if (useSchemeFilteredList) {
      return;
    }

    const competenciesArray = Array.isArray(competencies)
      ? competencies
      : (competencies as any)?.competencies || [];
    onPageLoaded(competenciesArray, competencyPagination);
  }, [competencies, competencyPagination, onPageLoaded, useSchemeFilteredList]);

  // Initialize and reset modal state on open/close
  useEffect(() => {
    if (open) {
      const competenciesToUse =
        propInitialCompetencies || [];

      setSelectedCompetencies(competenciesToUse);
      setInitialCompetencies(competenciesToUse);

      // Initialize levels from incoming data when in activity context
      if (hasLevels) {
        const levelMap: Record<string, AuthorizationLevel> = {} as any;
        competenciesToUse.forEach((c) => {
          if (c && c.level) {
            levelMap[c.id as any] = c.level as AuthorizationLevel;
          }
        });
        setCompetencyLevels(levelMap);
      } else {
        setCompetencyLevels({});
      }

      // Reset edit-related transient state
      setEditingRow(null);
      setNewCompetencyData({ name: '', externalId: '', description: '' });
      setTempCompetencies([]);
      setSearchValue('');
      setFilterByScheme(effectiveStandardTemplateIds.length > 0);
    } else {
      // Clear state when closed
      setSelectedCompetencies([]);
      setInitialCompetencies([]);
      setCompetencyLevels({});
      setEditingRow(null);
      setNewCompetencyData({ name: '', externalId: '', description: '' });
      setTempCompetencies([]);
      setSearchValue('');
      setFilterByScheme(true);
    }
  }, [open, activityId, propInitialCompetencies, effectiveStandardTemplateIds]);

  const handleSchemeFilterToggle = useCallback(
    (schemeOnly: boolean) => {
      setFilterByScheme(schemeOnly);
      onReset();
    },
    [onReset],
  );

  const schemeFilterLabel = useMemo(() => {
    const formatted = formatSchemeLabels(code ? code.split(',') : []);
    return formatted || t('modal.filter.thisScheme');
  }, [code, t]);

  const loadMore = useCallback(() => {
    if (useSchemeFilteredList) {
      return;
    }

    if (competencyData.length < total) {
      onPageChange(page + 1);
    }
  }, [competencyData.length, total, page, onPageChange, useSchemeFilteredList]);

  const handleCompetencyToggle = useCallback((competency: ICompetencyItem) => {
    setSelectedCompetencies((prev) => {
      const isSelected = prev.some((item) => item.id === competency.id);
      if (isSelected) {
        return prev.filter((item) => item.id !== competency.id);
      } else {
        return [...prev, competency];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedCompetencies.length === displayCompetencies.length) {
      setSelectedCompetencies([]);
    } else {
      setSelectedCompetencies([...displayCompetencies]);
    }
  }, [selectedCompetencies.length, displayCompetencies]);

  const handleLevelChange = useCallback(
    (competencyId: string | number, level: AuthorizationLevel | undefined) => {
      setCompetencyLevels((prev) => ({
        ...prev,
        [competencyId]: level === undefined ? null : level,
      }));
    },
    [],
  );

  const handleSaveCompetency = async () => {
    if (!newCompetencyData.name.trim()) {
      handleCancelEdit();
      return;
    }

    await axios.post(endpoints.competency.create, {
      ...newCompetencyData,
      name: `${code ? `${code}/` : ''}${newCompetencyData.name}`,
    });
    competencyMutate();

    setEditingRow(null);
    setNewCompetencyData({
      name: '',
      externalId: '',
      description: '',
    });
    // Remove the temp row from temp competencies after successful save
    setTempCompetencies((prev) => prev.filter((item) => !item.isNew));
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setNewCompetencyData({
      name: '',
      externalId: '',
      description: '',
    });
    // Remove the temp row from temp competencies
    setTempCompetencies((prev) => prev.filter((item) => !item.isNew));
  };

  // Check if competencies have changed (selection or level when in activity context)
  const hasCompetenciesChanged = () => {
    // Selection changed
    if (selectedCompetencies.length !== initialCompetencies.length) {
      return true;
    }

    const selectedIds = selectedCompetencies.map((c) => c.id).sort();
    const initialIds = initialCompetencies.map((c) => c.id).sort();

    if (selectedIds.length !== initialIds.length) {
      return true;
    }

    for (let i = 0; i < selectedIds.length; i++) {
      if (selectedIds[i] !== initialIds[i]) {
        return true;
      }
    }

    // Levels changed (only relevant when assigning to an activity)
    if (hasLevels) {
      const initialLevelById: Record<string | number, AuthorizationLevel | undefined> = {};
      initialCompetencies.forEach((c) => {
        initialLevelById[c.id] = c.level;
      });

      for (const c of selectedCompetencies) {
        const hasOverride = Object.prototype.hasOwnProperty.call(competencyLevels, c.id as any);
        const currentLevel = hasOverride ? (competencyLevels as any)[c.id] : c.level;
        if (currentLevel !== initialLevelById[c.id]) {
          return true;
        }
      }
    }

    return false;
  };

  const handleSave = () => {
    if (onSave) {
      // Include levels if hasLevels is true (for both activities and schemes)
      const payload = (activityId || hasLevels)
        ? selectedCompetencies.map((c) => {
            const hasOverride = Object.prototype.hasOwnProperty.call(competencyLevels, c.id as any);
            return {
              ...c,
              level: hasOverride ? (competencyLevels as any)[c.id] : c.level ?? null,
            };
          })
        : selectedCompetencies;
      onSave(payload);
    }
    onClose();
  };

  const renderCompetencyItem = useCallback(
    (index: number) => {
      const competency = displayCompetencies[index];
      if (!competency) return null;

      const isSelected = selectedCompetencies.some((item) => item.id === competency.id);
      const isEditing = editingRow?.id === competency.id && competency.isNew;

      return (
        <ListItem
          sx={{
            p: 0,
            minHeight: 'auto',
          }}
        >
          {isEditing ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 0.5,
                border: '1px dashed',
                borderColor: 'primary.main',
                borderRadius: 1,
              }}
            >
              <Checkbox disabled sx={{ mr: 1 }} />
              <TextField
                fullWidth
                value={newCompetencyData.name}
                onChange={(e) =>
                  setNewCompetencyData((prev) => ({ ...prev, name: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveCompetency();
                  }
                  if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                placeholder={t('modal.editing.namePlaceholder')}
                variant="outlined"
                size="small"
                sx={{ flex: 1 }}
                autoFocus
              />
              <TextField
                fullWidth
                value={newCompetencyData.externalId}
                onChange={(e) =>
                  setNewCompetencyData((prev) => ({ ...prev, externalId: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveCompetency();
                  }
                  if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                placeholder={t('modal.editing.externalIdPlaceholder')}
                variant="outlined"
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                fullWidth
                value={newCompetencyData.description}
                onChange={(e) =>
                  setNewCompetencyData((prev) => ({ ...prev, description: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveCompetency();
                  }
                  if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                placeholder={t('modal.editing.descriptionPlaceholder')}
                variant="outlined"
                size="small"
                sx={{ flex: 2 }}
              />
              {hasLevels && !competency.isNew && (
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={
                      Object.prototype.hasOwnProperty.call(competencyLevels, competency.id as any)
                        ? ((competencyLevels as any)[competency.id] ?? undefined)
                        : (competency.level ?? undefined)
                    }
                    onChange={(e) =>
                      handleLevelChange(competency.id, e.target.value as AuthorizationLevel)
                    }
                    variant="standard"
                    sx={{
                      boxShadow: 'none',
                      '&:before': { borderBottom: 'none !important' },
                      '&:after': { borderBottom: 'none !important' },
                      '&:hover:not(.Mui-disabled):before': { borderBottom: 'none !important' },
                      '& .MuiSelect-select': {
                        backgroundColor: 'transparent !important',
                      },
                      '& .MuiSelect-select:focus': {
                        backgroundColor: 'transparent !important',
                      },
                    }}
                  >
                    <MenuItem value={undefined}>
                      <em>{t('modal.level.remove')}</em>
                    </MenuItem>
                    <MenuItem value={AuthorizationLevel.LEAD}>{t('modal.level.lead')}</MenuItem>
                    <MenuItem value={AuthorizationLevel.COMPETENT}>{t('modal.level.competent')}</MenuItem>
                    <MenuItem value={AuthorizationLevel.EXPERT}>{t('modal.level.expert')}</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={handleCancelEdit}>
                  {t('common:actions.cancel')}
                </Button>
                <Button size="small" variant="contained" onClick={handleSaveCompetency}>
                  {t('common:actions.save')}
                </Button>
              </Box>
            </Box>
          ) : (
            <ListItemButton onClick={() => handleCompetencyToggle(competency)} sx={{ px: 1 }}>
              <Checkbox
                checked={isSelected}
                onChange={() => handleCompetencyToggle(competency)}
                onClick={(e) => e.stopPropagation()}
                sx={{ mr: 1 }}
              />
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                      {getCompetencyName(competency as CompetencyDTO)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      {competency.externalId || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 2 }}>
                      {competency.description || '-'}
                    </Typography>
                    {hasLevels && (
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <Select
                          value={
                            Object.prototype.hasOwnProperty.call(competencyLevels, competency.id as any)
                              ? ((competencyLevels as any)[competency.id] ?? undefined)
                              : (competency.level ?? undefined)
                          }
                          onChange={(e) =>
                            handleLevelChange(competency.id, e.target.value as AuthorizationLevel)
                          }
                          variant="standard"
                          sx={{
                            boxShadow: 'none',
                            '&:before': { borderBottom: 'none !important' },
                            '&:after': { borderBottom: 'none !important' },
                            '&:hover:not(.Mui-disabled):before': { borderBottom: 'none !important' },
                            '& .MuiSelect-select': {
                              backgroundColor: 'transparent !important',
                            },
                            '& .MuiSelect-select:focus': {
                              backgroundColor: 'transparent !important',
                            },
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MenuItem value={undefined}>
                            <em>{t('modal.level.remove')}</em>
                          </MenuItem>
                          <MenuItem value={AuthorizationLevel.LEAD}>{t('modal.level.lead')}</MenuItem>
                          <MenuItem value={AuthorizationLevel.COMPETENT}>{t('modal.level.competent')}</MenuItem>
                          <MenuItem value={AuthorizationLevel.EXPERT}>{t('modal.level.expert')}</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          )}
        </ListItem>
      );
    },
    [
      displayCompetencies,
      selectedCompetencies,
      handleCompetencyToggle,
      editingRow,
      newCompetencyData,
      handleSaveCompetency,
      handleCancelEdit,
      activityId,
      competencyLevels,
      handleLevelChange,
    ]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '90%',
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">{t('modal.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
          {hasLevels ? t('modal.subtitle.activity') : t('modal.subtitle.standard')}
        </Typography>
        {selectedCompetencies.length > 0 &&
          initialCompetencies.length !== selectedCompetencies.length && (
            <Alert severity="error" sx={{ backgroundColor: 'transparent', pl: 0 }}>
              <Typography variant="body2">{t('modal.changesAlert')}</Typography>
            </Alert>
          )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            fullWidth
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (!useSchemeFilteredList) {
                onReset();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="material-symbols:search-rounded" />
                </InputAdornment>
              ),
            }}
            placeholder={t('modal.search.placeholder')}
            variant="outlined"
            size="small"
          />
          {effectiveStandardTemplateIds.length > 0 && (
            <FormControlLabel
              control={
                <Switch
                  checked={filterByScheme}
                  onChange={(e) => handleSchemeFilterToggle(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('modal.filter.schemeOnly')}</Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {filterByScheme
                      ? t('modal.filter.schemeOnlyHint', { scheme: schemeFilterLabel })
                      : t('modal.filter.showAllHint')}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', height: '20px', gap: 1, mb: 1, px: 1 }}>
          <Checkbox
            checked={
              selectedCompetencies.length === displayCompetencies.length &&
              displayCompetencies.length > 0
            }
            indeterminate={
              selectedCompetencies.length > 0 &&
              selectedCompetencies.length < displayCompetencies.length
            }
            onChange={handleSelectAll}
          />
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {t('modal.headers.name')}
          </Typography>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {t('modal.headers.externalId')}
          </Typography>
          <Typography variant="subtitle2" sx={{ flex: 2 }}>
            {t('modal.headers.description')}
          </Typography>
          {hasLevels && (
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {t('modal.headers.level')}
            </Typography>
          )}
        </Box>

        <Divider />

        <Box sx={{ height: 400, width: '100%' }}>
          {catalogLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('modal.states.loading')}
              </Typography>
            </Box>
          ) : catalogError ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="error">
                {t('modal.error.load', { message: catalogError.message || t('modal.error.unknown') })}
              </Typography>
            </Box>
          ) : displayCompetencies.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t(useSchemeFilteredList ? 'modal.states.noSchemeData' : 'modal.states.noData')}
              </Typography>
            </Box>
          ) : (
            <Virtuoso
              ref={virtuosoRef}
              data={displayCompetencies}
              itemContent={renderCompetencyItem}
              endReached={loadMore}
              overscan={200}
              components={{
                Footer: () =>
                  !useSchemeFilteredList && competencyData.length < catalogTotal ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('modal.states.loadingMore')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('modal.states.endOfList')}
                      </Typography>
                    </Box>
                  ),
              }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <Button variant="text" onClick={onClose}>
          {t('modal.actions.cancel')}
        </Button>
        <Button variant="contained" disabled={!hasCompetenciesChanged()} onClick={handleSave}>
          {t('modal.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
